import * as React from "react"
import { MeterStatsType, StatsValue } from "@/types"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import { useQueries, useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { DateRange } from "react-day-picker"

import { ChartDataType } from "@/types/billing"
import { ConsumerStatsOverviewType, MeterStatusType } from "@/types/meter"
import {
  getConsumerDemographicBarChartAnalytics,
  getConsumerDemographicPieChartAnalytics,
  getConsumersTypesOverview,
  getConsumptionTrendAreaAnalytics,
  getConsumptionTrendPieAnalytics,
  getRevenueBarAnalytics,
  getRevenuePieAnalytics,
} from "@/lib/api/analytics"
import { canViewThisPage } from "@/lib/api/auth"
import {
  anualRevenueMockData,
  consumerClassMockData,
  consumptionTrendFormattedMock,
  consumptionTrendMock,
  mockStatsCardData,
  revenueBarGraphData,
} from "@/lib/mock-data"
import { getStatusStyle } from "@/lib/statusStyles"
import { abbreviateNumber } from "@/lib/utils"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AreaChartComponent } from "@/components/charts/areachart"
import { BarChartComponent } from "@/components/charts/barchart-horizontal"
import { PieChartComponent } from "@/components/charts/piechart"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import SelectTimeRange, {
  timeRangeOptions,
} from "@/components/select-time-range"
import { YearRangePicker } from "@/components/year-range-picker"

export const Route = createFileRoute("/_home/analytics/")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_himo_billing_analytics")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: AnalyticsPage,
})

const currentYear = new Date().getFullYear()
//get years in a past few years
const years = Array.from({ length: 30 }, (_, i) => `${currentYear - i}`)

function AnalyticsPage() {
  const [selectedRevenueYear, setSelectedRevenueYear] =
    React.useState<number>(currentYear)

  const [selectedConsumerDemographicYear, setSelectedConsumerDemographicYear] =
    React.useState<number>(currentYear)

  const [selectedConsumptionLineYears, setSelectedConsumptionLineYears] =
    React.useState<string[]>([String(currentYear)])

  const [consumptionPieDate, setConsumptionPieDate] = React.useState<DateRange>(
    {
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    },
  )

  const [consumptionTrendPieDate, setConsumptionTrendPieDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const [activeTab, setActiveTab] = React.useState("Revenue Profile")

  const [timeRange, setTimeRange] = React.useState(timeRangeOptions()[0])
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })

  /* =========================================================== */
  /*                     Revenue Profile Data                    */
  /* =========================================================== */
  const {
    data: revenueBarAnalytics,
    isLoading: isRevenueBarAnalyticsDataLoading,
  } = useQuery({
    queryKey: ["revenueBarAnalytics", selectedRevenueYear],
    queryFn: () => getRevenueBarAnalytics(selectedRevenueYear),
  })

  const {
    data: revenuePieAnalytics,
    isLoading: isRevenuePieAnalyticsDataLoading,
  } = useQuery({
    queryKey: ["revenuePieAnalytics", selectedRevenueYear],
    queryFn: () => getRevenuePieAnalytics(selectedRevenueYear),
  })

  /* =========================================================== */
  /*                  Consumption Trends Data                    */
  /* =========================================================== */

  const {
    data: consumptionTrendPieAnalytics,
    isLoading: isConsumptionTrendPieAnalyticsDataLoading,
  } = useQuery({
    queryKey: [
      "consumptionTrendPieAnalytics",
      consumptionTrendPieDate.from?.toISOString(),
      consumptionTrendPieDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = consumptionTrendPieDate.from ?? new Date()
      const to = consumptionTrendPieDate.to ?? new Date()
      return await getConsumptionTrendPieAnalytics(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const {
    data: consumptionTrendAreaAnalytics,
    isLoading: isConsumptionTrendAreaAnalyticsDataLoading,
  } = useQuery({
    queryKey: ["consumptionTrendAreaAnalytics", selectedConsumptionLineYears],
    queryFn: () =>
      getConsumptionTrendAreaAnalytics(selectedConsumptionLineYears),
  })

  /* =========================================================== */
  /*                  Consumer Demographic Data                    */
  /* =========================================================== */
  const {
    data: consumerDemographicPieChartData,
    isLoading: isConsumerDemographicPieChartDataLoading,
  } = useQuery({
    queryKey: [
      "consumerDemographicPieChartData",
      selectedConsumerDemographicYear,
    ],
    queryFn: () =>
      getConsumerDemographicPieChartAnalytics(selectedConsumerDemographicYear),
  })

  const {
    data: consumerDemographicBarChartData,
    isLoading: isConsumerDemographicBarChartDataLoading,
  } = useQuery({
    queryKey: [
      "consumerDemographicBarChartData",
      selectedConsumerDemographicYear,
    ],
    queryFn: () =>
      getConsumerDemographicBarChartAnalytics(selectedConsumerDemographicYear),
  })

  /* =========================================================== */
  /*                   Consumer Overview Data                    */
  /* =========================================================== */
  const {
    data: consumersTypesOverviewData,
    isLoading: isConsumersTypesOverviewDataLoading,
  } = useQuery<ConsumerStatsOverviewType>({
    queryKey: ["consumersTypesOverviewData", timeRange.value],
    queryFn: () => getConsumersTypesOverview(Number(timeRange.value)),
  })

  const formattedConsumptionTrendData = consumptionTrendAreaAnalytics?.data
    ? consumptionTrendAreaAnalytics.data.map((entry: any) => {
        const newEntry: any = { time: entry.time }

        Object.keys(entry).forEach((key) => {
          if (key !== "time") {
            const newKey = key.replace(/\s+/g, "") // Remove spaces
            newEntry[newKey] = entry[key]
          }
        })

        return newEntry
      })
    : []

  const renderOverviewCard = (
    title: string,
    icon: keyof typeof Icons,
    data?: {
      total: number | string
      percentage: number | null
      status: string
    },
  ) => {
    const IconComponent = Icons[icon]
    const {
      color,
      bgColor,
      icon: StatusIcon,
    } = getStatusStyle(data?.status ?? "", data?.percentage ?? null)

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex gap-2 text-[13px] font-medium text-muted-foreground">
            <IconComponent className="h-[18px] w-[18px] text-primary" />

            <p className="line-height-[14px] font-worksans font-bold tracking-wider">
              {title}
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-2xl font-bold">
          {isConsumersTypesOverviewDataLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : data?.total !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <h1 className="font-semibold">
                {title === "Total Payments" ? `PHP ${data.total}` : data.total}
              </h1>
              {data.status !== "New" && (
                <span
                  className={`rounded-sm px-1 text-[13px] font-normal ${color} ${bgColor}`}
                >
                  {data.status !== "Flat" && data.percentage !== null
                    ? `${data.percentage.toFixed(2)}%`
                    : ""}

                  {StatusIcon && (
                    <span
                      className={
                        data.status === "Flat"
                          ? "flex items-center"
                          : "inline-block px-1"
                      }
                    >
                      {StatusIcon}
                    </span>
                  )}
                </span>
              )}
            </div>
          ) : (
            "N/A"
          )}
        </CardContent>
      </Card>
    )
  }
  const items = Object.entries(consumersTypesOverviewData?.data || {})
  const itemsCount = items.length

  // Dynamically decide how many items per row
  let itemsPerRow =
    itemsCount <= 4 ? 4 : itemsCount <= 6 ? 6 : Math.ceil(itemsCount / 2)

  // Adjust itemsPerRow if it's 5 to be 6
  itemsPerRow = itemsPerRow === 5 ? 6 : itemsPerRow

  console.log(itemsPerRow)

  const getColSpan = (count: number) => {
    if (count === 0) return 1

    return Math.round(12 / count) // Better fit than floor()
  }

  return (
    <>
      <main className="space-y-4 p-4">
        <div className="flex w-full justify-between pt-4">
          <div>
            <Header>Analytics</Header>
          </div>

          <div className="flex items-center gap-3">
            <Label>Set view</Label>
            <SelectTimeRange range={timeRange} setRange={setTimeRange} />
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {(isConsumersTypesOverviewDataLoading
            ? Array.from({ length: itemsPerRow })
            : items
          ).map((entry: any, index) => {
            const key = isConsumersTypesOverviewDataLoading ? index : entry[0]
            const consumer = isConsumersTypesOverviewDataLoading
              ? undefined
              : entry?.[1]

            const currentRow = index < itemsPerRow ? 0 : 1
            const itemsInThisRow =
              currentRow === 0 ? itemsPerRow : itemsCount - itemsPerRow

            const colSpan = getColSpan(itemsInThisRow)

            return (
              <div
                key={key}
                className={`col-span-12 w-full sm:col-span-6 md:col-span-${colSpan}`}
              >
                {renderOverviewCard(
                  consumer?.label ?? "Loading...",
                  consumer?.icon ?? "loader",
                  consumer
                    ? {
                        total: consumer.total,
                        percentage: consumer.percentage,
                        status: consumer.status,
                      }
                    : undefined,
                )}
              </div>
            )
          })}
        </div>

        <div>
          <div className="flex h-full flex-col">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="h-full"
            >
              <div className="flex items-center justify-between overflow-x-auto p-0">
                <TabsList className="m-0 flex h-12 w-max items-center overflow-hidden rounded-lg border-2 bg-white p-0">
                  <TabsTrigger
                    value="Revenue Profile"
                    className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                  >
                    Revenue Profile
                  </TabsTrigger>
                  <TabsTrigger
                    value="Consumption Trends"
                    className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                  >
                    Consumption Trends
                  </TabsTrigger>
                  <TabsTrigger
                    value="Consumer Demographic"
                    className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                  >
                    Consumer Demographic
                  </TabsTrigger>
                </TabsList>

                <div
                  className={`${activeTab === "Consumption Trends" ? "hidden" : ""} w-[130px]`}
                >
                  <Select
                    value={
                      activeTab === "Revenue Profile"
                        ? selectedRevenueYear.toString()
                        : selectedConsumerDemographicYear.toString()
                    }
                    onValueChange={(value) => {
                      if (activeTab === "Revenue Profile") {
                        setSelectedRevenueYear(parseInt(value))
                      } else {
                        setSelectedConsumerDemographicYear(parseInt(value))
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem
                          className={`relative flex items-center justify-center py-2 pl-0 pr-0 after:absolute after:bottom-1 after:h-[1px] after:w-[80%] after:bg-gray-300 data-[state=checked]:bg-[#00B7AE] data-[state=checked]:text-white`}
                          key={year}
                          value={year}
                        >
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <TabsContent value="Revenue Profile" className="min-h-0 flex-1">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-12">
                  <Card className="col-span-7">
                    <CardHeader className="flex items-center justify-between md:flex-row">
                      <div className="flex flex-col gap-2">
                        <CardTitle>Top Revenue Consumers</CardTitle>
                        <div>
                          <span>{revenueBarAnalytics?.interpretation}</span>
                        </div>
                      </div>
                    </CardHeader>
                    <DropdownMenuSeparator className="mx-4 my-2 h-px bg-muted-foreground/30" />

                    <CardContent>
                      {isRevenueBarAnalyticsDataLoading ? (
                        <div className="flex h-[240px] items-center justify-center">
                          <Skeleton className="h-full w-full" />
                        </div>
                      ) : revenueBarAnalytics &&
                        revenueBarAnalytics?.data.length > 0 ? (
                        <BarChartComponent
                          //data={revenueBarGraphData.data}
                          data={revenueBarAnalytics?.data}
                          xAxisKey="account"
                          //toggledKeys={[connection_type]}
                        />
                      ) : (
                        <div className="flex h-[240px] items-center justify-center text-gray-500">
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="col-span-5">
                    <CardTitle className="text-xxl px-4 py-4">
                      Annual Revenue Statistics
                    </CardTitle>
                    <CardContent className="flex flex-col gap-2">
                      <Card>
                        <CardHeader className="flex items-center justify-between px-4 py-2 md:flex-row">
                          <div className="flex gap-2">
                            <div>
                              <span className="text-5xl font-bold text-primary">
                                {abbreviateNumber(
                                  revenueBarAnalytics?.total_annual_revenue ??
                                    0,
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <span className="font-semibold text-primary">
                                PHP
                              </span>
                              in annual revenue.
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                      <Card>
                        <CardHeader className="flex items-center justify-between md:flex-row">
                          {revenuePieAnalytics?.highest?.percentage > 0 ? (
                            <div className="flex flex-col gap-2">
                              <div>
                                <span className="text-primary">
                                  {`${revenuePieAnalytics?.highest?.percentage.toFixed(2)}% of annual revenue`}
                                </span>{" "}
                                {`comes from ${revenuePieAnalytics?.highest?.connection_type} SCs.`}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                        </CardHeader>
                        <CardContent>
                          {isRevenuePieAnalyticsDataLoading ? (
                            <div className="flex h-[240px] items-center justify-center">
                              <Skeleton className="h-full w-full" />
                            </div>
                          ) : revenuePieAnalytics &&
                            revenuePieAnalytics?.data.length > 0 ? (
                            <div className="flex h-full items-center justify-center">
                              <PieChartComponent
                                data={revenuePieAnalytics?.data}
                                dataKey="total_amount"
                                nameKey="connection_type"
                                //className="height:1000px"
                              />
                            </div>
                          ) : (
                            <div className="flex h-[240px] items-center justify-center text-gray-500">
                              No data available
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent
                value="Consumption Trends"
                className="min-h-0 flex-1"
              >
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-12">
                  <Card className="col-span-7">
                    <CardHeader className="flex items-center justify-between md:flex-row">
                      <div className="flex flex-col gap-2">
                        <CardTitle>Consumption Trends</CardTitle>
                        <div className="text-muted-foreground">
                          {
                            consumptionTrendAreaAnalytics?.analytics?.overall
                              ?.trend_message
                          }
                        </div>
                      </div>

                      <div>
                        <YearRangePicker
                          selectedYears={selectedConsumptionLineYears}
                          setSelectedYears={setSelectedConsumptionLineYears}
                        />
                      </div>
                    </CardHeader>

                    <DropdownMenuSeparator className="mx-4 my-2 h-px bg-muted-foreground/30" />
                    <CardContent className="overflow-x-auto">
                      {isConsumptionTrendAreaAnalyticsDataLoading ? (
                        <div className="flex h-[240px] items-center justify-center">
                          <Skeleton className="h-full w-full" />
                        </div>
                      ) : formattedConsumptionTrendData.length > 0 ? (
                        <div className="min-w-[1000px]">
                          <AreaChartComponent
                            data={formattedConsumptionTrendData}
                            className="consumption-trends"
                          />
                        </div>
                      ) : (
                        <div className="flex h-[240px] items-center justify-center text-gray-500">
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="col-span-5">
                    <CardHeader className="flex items-center justify-between md:flex-row">
                      <CardTitle>Consumption Statistics</CardTitle>

                      <div>
                        <DateRangePicker
                          date={consumptionTrendPieDate}
                          setDate={setConsumptionTrendPieDate}
                        />
                      </div>
                    </CardHeader>

                    <CardContent className="flex flex-col gap-2">
                      <Card>
                        <CardHeader className="flex items-center justify-between px-4 py-2 md:flex-row">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <div>
                              <span className="text-5xl font-bold text-primary">
                                1029
                              </span>
                            </div>
                            <div className="text-center">
                              units of water usage consumed in{" "}
                              <span className="font-semibold text-primary">
                                2025
                              </span>{" "}
                              .
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                      <Card>
                        <CardHeader className="flex items-center justify-between md:flex-row">
                          <div className="flex flex-col gap-2">
                            <div>
                              {
                                consumptionTrendAreaAnalytics?.analytics
                                  ?.overall?.high_consumption_message
                              }
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isConsumptionTrendPieAnalyticsDataLoading ? (
                            <div className="flex h-[240px] items-center justify-center">
                              <Skeleton className="h-full w-full" />
                            </div>
                          ) : (
                            <div className="flex h-full items-center justify-center">
                              <PieChartComponent
                                data={consumptionTrendPieAnalytics}
                                dataKey="total_consumption"
                                nameKey="connection_type"
                                //className="height:1000px"
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent
                value="Consumer Demographic"
                className="min-h-0 flex-1"
              >
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-12">
                  <Card className="col-span-7">
                    <CardHeader className="flex items-center justify-between md:flex-row">
                      <div className="flex flex-col gap-2">
                        <CardTitle>Consumer Demographic</CardTitle>
                        {consumerDemographicBarChartData?.[1]
                          ?.bar_chart_analytics.largest_water_consumer
                          ?.percentage > 0 ? (
                          <div>
                            <span className="text-primary">
                              {" "}
                              {
                                consumerDemographicBarChartData?.[1]
                                  ?.bar_chart_analytics.largest_water_consumer
                                  .consumer_class
                              }
                            </span>{" "}
                            are the largest consumers of water, consuming more
                            than the average amount.
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </CardHeader>

                    <DropdownMenuSeparator className="mx-4 my-2 h-px bg-muted-foreground/30" />

                    <CardContent>
                      {isConsumerDemographicBarChartDataLoading ? (
                        <div className="flex h-[240px] items-center justify-center">
                          <Skeleton className="h-full w-full" />
                        </div>
                      ) : (
                        <BarChartComponent
                          data={
                            consumerDemographicBarChartData?.[1]
                              ?.bar_chart_analytics
                              .consumption_total_per_consumer_class
                            /* consumerClassMockData */
                          }
                          xAxisKey="consumer_class"
                          //toggledKeys={["total_current_consumption"]}
                        />
                      )}
                    </CardContent>
                  </Card>
                  <Card className="col-span-5">
                    <CardTitle className="text-xxl px-4 py-4">
                      Consumer Statistics
                    </CardTitle>
                    <CardContent className="flex flex-col gap-2">
                      <Card>
                        <CardHeader className="flex items-center justify-between px-4 py-2 md:flex-row">
                          {consumerDemographicPieChartData &&
                          consumerDemographicPieChartData?.[1]
                            ?.pie_chart_analytics?.chart_data ? (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <div>
                                <span className="text-5xl font-bold text-primary">
                                  {(
                                    consumerDemographicPieChartData?.[1]
                                      ?.pie_chart_analytics
                                      ?.largest_consumer_type.percentage ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </div>
                              <div className="text-center">
                                of total consumers are{" "}
                                <span className="text-primary">
                                  {
                                    consumerDemographicPieChartData?.[1]
                                      ?.pie_chart_analytics
                                      ?.largest_consumer_type.connection_type
                                  }
                                </span>
                                .
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                        </CardHeader>
                      </Card>
                      <Card>
                        <CardHeader className="flex items-center justify-between md:flex-row">
                          {consumerDemographicPieChartData &&
                          consumerDemographicPieChartData?.[1]
                            ?.pie_chart_analytics?.chart_data ? (
                            <div className="flex flex-col gap-2">
                              <div>
                                Among the remaining{" "}
                                <span className="text-primary">
                                  {100 -
                                    consumerDemographicPieChartData?.[1]
                                      ?.pie_chart_analytics
                                      ?.largest_consumer_type?.percentage}
                                  %
                                </span>{" "}
                                are{" "}
                                {consumerDemographicPieChartData?.[1]?.pie_chart_analytics?.chart_data
                                  .filter(
                                    (item: ChartDataType) =>
                                      item.connection_type !==
                                      consumerDemographicPieChartData?.[1]
                                        ?.pie_chart_analytics
                                        ?.largest_consumer_type
                                        ?.connection_type,
                                  )
                                  .map((item: ChartDataType, index: number) => (
                                    <span key={index} className="text-primary">
                                      {item.connection_type} ({item.percentage}
                                      %)
                                      {index < 2 ? ", " : ""}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                        </CardHeader>
                        <CardContent>
                          {isConsumerDemographicPieChartDataLoading ? (
                            <div className="flex h-[240px] items-center justify-center">
                              <Skeleton className="h-full w-full" />
                            </div>
                          ) : consumerDemographicPieChartData &&
                            consumerDemographicPieChartData?.[1]
                              ?.pie_chart_analytics?.chart_data ? (
                            <div className="flex h-full items-center justify-center">
                              <PieChartComponent
                                data={
                                  consumerDemographicPieChartData?.[1]
                                    ?.pie_chart_analytics?.chart_data
                                }
                                dataKey="count"
                                nameKey="connection_type"
                              />
                            </div>
                          ) : (
                            <div className="flex h-[240px] items-center justify-center text-gray-500">
                              No data available
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </>
  )
}
